import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Radio, Zap, AlertTriangle, TrendingUp, Wallet, ClipboardList } from "lucide-react";

const T = {
  tr: {
    liveMode: "CANLI Modu",
    learningMode: "Öğrenme Modu",
    archiveMode: "Arşiv Modu",
    today: "Bugün",
    revenue: "Ciro", expenses: "Gider", payments: "Ödeme",
    events: "olay",
    cashPosition: "Anlık Nakit Pozisyonu",
    currentBank: "Banka", currentCash: "Kasa",
    availableCash: "Kullanılabilir", eodCash: "Gün sonu", eowCash: "Hafta sonu",
    payroll7d: "7g Bordro", taxes7d: "7g Vergi", pendingSup: "Bekleyen Tedarikçi",
    assistant: "Operasyon Rehberi", noTips: "Bugün için önemli bir uyarı yok.",
  },
  de: {
    liveMode: "LIVE-Modus",
    learningMode: "Lernmodus",
    archiveMode: "Archivmodus",
    today: "Heute",
    revenue: "Umsatz", expenses: "Ausgaben", payments: "Zahlungen",
    events: "Ereignisse",
    cashPosition: "Echtzeit-Liquidität",
    currentBank: "Bank", currentCash: "Kasse",
    availableCash: "Verfügbar", eodCash: "Tagesende", eowCash: "Wochenende",
    payroll7d: "7T Lohn", taxes7d: "7T Steuern", pendingSup: "Offene Lieferanten",
    assistant: "Betriebsempfehlungen", noTips: "Heute keine wichtigen Hinweise.",
  },
};

const MODE_LABEL = (t, mode) => ({live: t.liveMode, learning: t.learningMode, archive: t.archiveMode}[mode] || t.learningMode);
const MODE_ACCENT = { live: "bg-emerald-100 text-emerald-800 border-emerald-200",
                      learning: "bg-amber-100 text-amber-800 border-amber-200",
                      archive: "bg-slate-200 text-slate-800 border-slate-300" };

const PRIORITY_ACCENT = {
  critical: "border-rose-300 bg-rose-50",
  high:     "border-amber-300 bg-amber-50",
  medium:   "border-sky-300 bg-sky-50",
  low:      "border-slate-200 bg-slate-50",
};
const PRIORITY_ICON = { critical: AlertTriangle, high: AlertTriangle, medium: Zap, low: ClipboardList };

export default function LiveTodayCard() {
  const { lang } = useI18n();
  const t = T[lang] || T.tr;
  const [mode, setMode] = useState(null);
  const [today, setToday] = useState(null);
  const [cash, setCash] = useState(null);
  const [tips, setTips] = useState([]);

  const load = async () => {
    try {
      const [m, td, cp, as] = await Promise.all([
        api.get("/live/mode"),
        api.get("/live/today"),
        api.get("/live/cash-position"),
        api.get("/live/assistant"),
      ]);
      setMode(m.data); setToday(td.data); setCash(cp.data); setTips(as.data?.tips || []);
    } catch (_e) { /* ignore transient network errors */ }
  };
  useEffect(() => { load(); const iv = setInterval(load, 60_000); return () => clearInterval(iv); }, []);

  if (!mode) return null;
  const currentMode = mode.current_mode || "learning";

  return (
    <div className="space-y-4" data-testid="live-today-card">
      {/* Mode + Today header */}
      <Card className="border-primary/40 bg-gradient-to-r from-primary/5 to-transparent">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Radio className={`w-4 h-4 ${currentMode === "live" ? "text-emerald-600 animate-pulse" : "text-muted-foreground"}`} />
              {t.today} · <span className="font-mono">{today?.date}</span>
            </span>
            <Badge variant="outline" className={MODE_ACCENT[currentMode]} data-testid="live-mode-badge">
              {MODE_LABEL(t, currentMode)}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label={t.revenue} value={money(today?.revenue_total || 0)} icon={TrendingUp} accent="emerald"
                testid="today-revenue" sub={`${today?.revenue_events_count || 0} ${t.events}`} />
          <Stat label={t.expenses} value={money(today?.expenses_total || 0)} accent="rose"
                testid="today-expenses" sub={`${today?.expense_events_count || 0} ${t.events}`} />
          <Stat label={t.payments} value={money(today?.payments_total || 0)}
                testid="today-payments" />
          <Stat label={t.events} value={today?.events_count || 0} icon={Zap}
                testid="today-events" />
        </CardContent>
      </Card>

      {/* Cash position */}
      {cash && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="w-4 h-4 text-primary" />{t.cashPosition}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label={t.currentBank} value={money(cash.current_bank)} testid="cash-bank" />
            <Stat label={t.currentCash} value={money(cash.current_cash)} testid="cash-cash" />
            <Stat label={t.availableCash} value={money(cash.available_cash)}
                  accent={cash.available_cash > 0 ? "emerald" : "rose"}
                  testid="cash-available" />
            <Stat label={t.eodCash} value={money(cash.projected_eod_cash)} testid="cash-eod" />
            <Stat label={t.eowCash} value={money(cash.projected_eow_cash)}
                  accent={cash.projected_eow_cash > 0 ? undefined : "rose"}
                  testid="cash-eow" />
            <Stat label={t.pendingSup} value={money(cash.pending_supplier_payments)}
                  accent={cash.pending_supplier_payments > 0 ? "amber" : undefined}
                  testid="cash-pending-suppliers" />
            <Stat label={t.payroll7d} value={money(cash.upcoming_payroll_7d)} testid="cash-payroll-7d" />
            <Stat label={t.taxes7d} value={money(cash.upcoming_taxes_7d)} testid="cash-taxes-7d" />
          </CardContent>
        </Card>
      )}

      {/* Owner assistant */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="w-4 h-4 text-primary" />{t.assistant}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {tips.length === 0 && (
            <div className="text-xs text-muted-foreground py-4 text-center" data-testid="assistant-empty">
              {t.noTips}
            </div>
          )}
          {tips.map((tip, i) => {
            const Icon = PRIORITY_ICON[tip.priority] || Zap;
            return (
              <div key={i}
                   data-testid={`assistant-tip-${tip.priority}`}
                   className={`border rounded-lg p-3 text-sm flex items-start gap-3 ${PRIORITY_ACCENT[tip.priority] || ""}`}>
                <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{tip.topic}</div>
                  <div>{tip.message}</div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, sub, accent, icon: Icon, testid }) {
  const color = accent === "emerald" ? "text-emerald-700"
              : accent === "rose"    ? "text-rose-600"
              : accent === "amber"   ? "text-amber-700"
              : "text-foreground";
  return (
    <div className="border rounded-xl p-3 bg-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground" />}
      </div>
      <div className={`text-xl font-semibold ${color}`} data-testid={testid}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
